/* begin general import */
import { openOnRowClick } from "helpers/row-click";
import { Card, Col, Dropdown, Menu, Row, Tag } from "antd";
import Table, { ColumnProps } from "antd/lib/table";
import classNames from "classnames";
import InputSearch from "components/Utility/InputSearch/InputSearch";
import Pagination from "components/Utility/Pagination/Pagination";
import moment from "moment";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import masterService from "services/pages/master-service";
import nameof from "ts-nameof.macro";
/* end general import */

/* begin filter import */
/* end filter import */

/* begin individual import */
import { Device } from "models/Device";
import { DeviceFilter } from "models/Device/DeviceFilter";
import { deviceRepository } from "repositories/device-repository";
import { useLocation } from "react-router";
import { DEVICE_TYPE_DETAIL_ROUTE, DEVICE_TYPE_MASTER_ROUTE, DORMITORY_ROUTE } from "config/route-consts";
import { Link } from "react-router-dom";
import { routerService } from "services/route-service";
import DeviceTypeDetailModal from "./DeviceTypeDetailModal";
import { deviceTypeMap } from "data/seeeds";
import AdvanceStringFilter from "components/Utility/AdvanceFilter/AdvanceStringFilter/AdvanceStringFilter";
import { StringFilter } from "@react3l/advanced-filters";
import { CSSTransition } from "react-transition-group";
/* end individual import */

function DeviceTypeDetail() {
  const [translate] = useTranslation();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const deviceTypeIdString = queryParams.get("deviceTypeId[equal]");
  const deviceTypeId = deviceTypeIdString ? Number(deviceTypeIdString) : null;
  const deviceTypeInfo = deviceTypeId
    ? deviceTypeMap.get(deviceTypeId)
    : undefined;

  const {
    list,
    total,
    loadingList,
    filter,
    toggle,
    handleUpdateNewFilter,
    handleTableChange,
    handlePagination,
    handleChangeFilter,
    handleToggleSearch,
  } = masterService.useMaster<Device, DeviceFilter>(
    DeviceFilter,
    DEVICE_TYPE_DETAIL_ROUTE,
    deviceRepository.list,
    deviceRepository.count
  );

  const {
    isOpenPreview,
    isLoadingPreview,
    previewModel,
    handleOpenPreview,
    handleClosePreview,
  } = masterService.usePreviewDevice<Device>(Device, deviceRepository.getFull);

  const [, handleGoDormitory] = routerService.useMasterNavigation(DORMITORY_ROUTE);

  const _handleChangeFilter = React.useCallback(
    (value) => {
      const newFilter = { ...filter };
      newFilter["search"] = value;
      handleUpdateNewFilter(newFilter);
    },
    [filter, handleUpdateNewFilter]
  );

  const menuAction = React.useCallback(
    (id: string, dormitoryId: string) => (
      <Menu>
        <Menu.Item key="1">
          <div
            className="ant-action-menu"
            onClick={handleOpenPreview(id, dormitoryId)}
          >
            {translate("general.actions.view")}
          </div>
        </Menu.Item>
      </Menu>
    ),
    [handleOpenPreview, translate]
  );

  const columns: ColumnProps<Device>[] = useMemo(
    () => [
      {
        title: (
          <div className="text-center gradient-text">
            {translate("Tên thiết bị")}
          </div>
        ),
        key: "name",
        dataIndex: "name",
        sorter: true,
        width: 180,
        render(name: string) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames(
                  "cell-master__first-row",
                  "first-row--link"
                )}
              >
                {name}
              </div>
            </div>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">{translate("ID")}</div>
        ),
        key: "id",
        dataIndex: "id",
        sorter: true,
        width: 120,
        ellipsis: true,
        render(id: string) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames("cell-master__first-row")}
                title={id}
              >
                {id?.substring(0, 8)}...
              </div>
            </div>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">
            {translate("Home Controller")}
          </div>
        ),
        key: "homeController",
        dataIndex: "homeController",
        width: 150,
        render(homeController: any) {
          return (
            <div className="ant-cell-master__container">
              <div className="cell-master__first-row">
                {homeController?.name || "-"}
              </div>
              {homeController?.macAddress && (
                <div className="cell-master__second-row" style={{ fontSize: "11px", color: "#888" }}>
                  {homeController.macAddress}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">
            {translate("IP Address")}
          </div>
        ),
        key: "ipAddress",
        dataIndex: "homeController",
        width: 130,
        render(homeController: any) {
          return (
            <div className="ant-cell-master__container">
              <div className="cell-master__first-row">
                {homeController?.ipAddress || "-"}
              </div>
            </div>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">
            {translate("Firmware")}
          </div>
        ),
        key: "version",
        dataIndex: "homeController",
        width: 100,
        render(homeController: any) {
          return (
            <div className="ant-cell-master__container">
              <div className="cell-master__first-row">
                {homeController?.version || "-"}
              </div>
            </div>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">
            {translate("Trạng thái HC")}
          </div>
        ),
        key: "hcStatus",
        dataIndex: "homeController",
        width: 120,
        render(homeController: any) {
          if (!homeController) return <span>-</span>;
          const activeStatus = homeController.homeControllerActiveStatus;
          const isOnline = activeStatus?.code === "ONLINE";
          return (
            <Tag color={isOnline ? "green" : "default"}>
              {activeStatus?.name || "-"}
            </Tag>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">{translate("Nhà")}</div>
        ),
        key: "dormitoryId",
        dataIndex: "dormitoryId",
        width: 120,
        ellipsis: true,
        render(dormitoryId: string) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames(
                  "cell-master__first-row",
                  "first-row--link",
                  "text-link"
                )}
                style={{ cursor: "pointer" }}
                onClick={handleGoDormitory(dormitoryId)}
                title={dormitoryId}
              >
                {dormitoryId ? `${dormitoryId.substring(0, 8)}...` : "-"}
              </div>
            </div>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">
            {translate("Ngày tạo")}
          </div>
        ),
        key: "createdAt",
        dataIndex: "createdAt",
        sorter: true,
        width: 120,
        render(createdAt: string) {
          return (
            <div className="ant-cell-master__container">
              <div className="cell-master__first-row">
                {createdAt ? moment(createdAt).format("DD/MM/YYYY") : "-"}
              </div>
              {createdAt && (
                <div className="cell-master__second-row" style={{ fontSize: "11px", color: "#888" }}>
                  {moment(createdAt).format("HH:mm")}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">
            {translate("Trạng thái")}
          </div>
        ),
        key: "status",
        dataIndex: "deletedAt",
        width: 100,
        render(deletedAt: string) {
          return (
            <Tag color={deletedAt ? "red" : "green"}>
              {deletedAt ? "Đã xóa" : "Hoạt động"}
            </Tag>
          );
        },
      },
      {
        title: (
          <div className="text-center gradient-text">
            {translate("general.actions.action")}
          </div>
        ),
        key: "action",
        dataIndex: "id",
        fixed: "right",
        width: 80,
        align: "center",
        render: (id: string, record: Device, index: number) => (
          <div className="d-flex justify-content-center button-action-table">
            <Dropdown
              overlay={menuAction(id, record.dormitoryId)}
              trigger={["click"]}
              placement="bottomCenter"
              arrow
            >
              <span className="action__dots">...</span>
            </Dropdown>
          </div>
        ),
      },
    ],
    [translate, menuAction, handleGoDormitory]
  );

  const filterChildren = useMemo(
    () => (
      <Row className="mt-4">
        <Col lg={4} className="pr-4">
          <label className="label">Tên thiết bị</label>
          <AdvanceStringFilter
            isMaterial={true}
            value={filter[nameof(list[0].name)]["contain"]}
            onEnter={handleChangeFilter(
              nameof(list[0].name),
              "contain" as any,
              StringFilter
            )}
            placeHolder="Tìm kiếm..."
          />
        </Col>
        <Col lg={4} className="pr-4">
          <label className="label">ID thiết bị</label>
          <AdvanceStringFilter
            isMaterial={true}
            value={filter[nameof(list[0].id)]["contain"]}
            onEnter={handleChangeFilter(
              nameof(list[0].id),
              "contain" as any,
              StringFilter
            )}
            placeHolder="Tìm kiếm..."
          />
        </Col>
      </Row>
    ),
    [filter, handleChangeFilter, list]
  );

  return (
    <>
      <div className="page page__master">
        <div className="page__header">
          <Link to={DEVICE_TYPE_MASTER_ROUTE} className="page-back">
            <i className="tio-chevron_left" aria-hidden />
            {translate("deviceTypes.master.title")}
          </Link>
          <div className="page__title">
            {deviceTypeInfo
              ? deviceTypeInfo.typeName
              : translate("deviceTypes.master.title")}
          </div>
        </div>
        <div className="page__search">
          <Card bordered={false}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex w-50">
                <div className="w70">
                  <InputSearch
                    value={filter["search"]}
                    onChange={_handleChangeFilter}
                    placeHolder="Tìm kiếm"
                  />
                </div>
                <button
                  className={classNames(
                    "btn component__btn-toggle ml-2",
                    toggle === true ? "component__btn-toggle-active" : ""
                  )}
                  onClick={handleToggleSearch}
                >
                  <i className="tio-tune_horizontal mr-2"></i>
                  <span className="component_btn-text">
                    {translate("general.button.advance")}
                  </span>
                </button>
              </div>
            </div>
            <CSSTransition
              in={toggle}
              timeout={100}
              classNames={"show"}
              unmountOnExit
            >
              {filterChildren}
            </CSSTransition>
          </Card>
        </div>
        <div className="page__master-table custom-scrollbar pb-4">
          <Card bordered={false}>
            <Table
              onRow={(record) => openOnRowClick(handleOpenPreview(record.id, record.dormitoryId))}
              rowKey={nameof(list[0].id)}
              columns={columns}
              pagination={false}
              dataSource={list}
              loading={loadingList}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
              title={() => (
                <>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="action__result">{total} Kết quả</span>
                    <div className="flex-shrink-1 d-flex align-items-center">
                      <Pagination
                        skip={filter.skip}
                        take={filter.take}
                        total={total}
                        onChange={handlePagination}
                        style={{ margin: "10px" }}
                      />
                    </div>
                  </div>
                </>
              )}
            />
          </Card>
        </div>
      </div>
      <DeviceTypeDetailModal
        previewModel={previewModel}
        isOpenPreview={isOpenPreview}
        isLoadingPreview={isLoadingPreview}
        handleClosePreview={handleClosePreview}
        translate={translate}
      />
    </>
  );
}

export default DeviceTypeDetail;
